"use client";

import { usePathname } from "next/navigation";
import Navbar from "./Navbar";
import Footer from "./Footer";
import LazyChat from "./LazyChat";

// Chat widget kill-switch. Set to true to bring the floating chat bubble
// back. Disabled while we focus on the contact form / quote flow as the
// primary lead path.
const CHAT_ENABLED = false;

export default function PublicShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // Admin and staff pages are fully self-contained — suppress public nav,
  // footer, and chat widget entirely.
  //
  // /crm is deliberately NOT here: it keeps the normal site header, which
  // already carries sign-out and the rest of the navigation. It has no header
  // of its own, so there is nothing to stack.
  // /dashboard is here for the same reason as /admin and /staff: it carries
  // its own sidebar, header and sign-in identity, so the public nav stacks a
  // second set of chrome on top of a screen that already has one.
  const isBare =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/staff") ||
    pathname.startsWith("/dashboard") ||
    // /pay is a customer settling one invoice from a texted link. The site
    // nav invites them to browse services and sign in, which is everything
    // they did not come to do.
    pathname.startsWith("/pay");
  // Order flow gets the nav + footer but no chat bubble — the chat covers the
  // sticky "Next" / sticky-selection bar on mobile and competes with the
  // primary task. Quote flow is the conversion path; don't distract from it.
  const hideChat = pathname.startsWith("/order") || !CHAT_ENABLED;
  // The design studio is an iframe that scrolls itself. Given a footer below
  // it, the page scrolls too and the two fight — you drag inside the canvas
  // and the page moves under you. So it keeps the nav and loses everything
  // else, and main is pinned to exactly the space under the nav with its own
  // overflow locked, leaving the iframe as the only thing that scrolls.
  const isFullBleed = pathname.startsWith("/designer");

  if (isBare) {
    return <>{children}</>;
  }

  if (isFullBleed) {
    return (
      <>
        <Navbar />
        {/* h-dvh over h-screen: on mobile, 100vh sits behind the browser
            chrome and the bottom of the studio would be unreachable. The
            padding is inside the height (border-box), so the child gets
            exactly the viewport minus the 95px nav. */}
        <main className="pt-[95px] h-dvh overflow-hidden">{children}</main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-[95px]">{children}</main>
      <Footer />
      {!hideChat && <LazyChat />}
    </>
  );
}
